/**
 * Blog - Central de Conteúdo
 * WorkStream Marketing Site
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Calendar, Clock, User } from 'lucide-react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string
  content?: string
  category: string
  tags: string[]
  author: {
    name: string
    avatar?: string
    role: string
  }
  publishedAt: Date
  readTime: number
  featured: boolean
  image?: string
}

const posts: BlogPost[] = [
  {
    id: '1',
    slug: 'nr-35-guia-completo',
    title: 'NR-35: Guia Completo sobre Trabalho em Altura',
    excerpt: 'Tudo que você precisa saber sobre a NR-35, desde requisitos legais até implementação prática com tecnologia.',
    category: 'NRs',
    tags: ['NR-35', 'Segurança', 'Trabalho em Altura'],
    author: { name: 'Carlos Mendes', role: 'Especialista em Segurança' },
    publishedAt: new Date('2025-11-25'),
    readTime: 12,
    featured: true,
  },
  {
    id: '2',
    slug: 'reconhecimento-facial-ponto',
    title: 'Como o Reconhecimento Facial Está Transformando o Controle de Ponto',
    excerpt: 'Descubra como empresas estão eliminando fraudes e ganhando produtividade com ponto facial.',
    category: 'Tecnologia',
    tags: ['Reconhecimento Facial', 'Ponto', 'IA'],
    author: { name: 'Ana Silva', role: 'Product Manager' },
    publishedAt: new Date('2025-11-22'),
    readTime: 8,
    featured: true,
  },
  {
    id: '3',
    slug: 'epi-deteccao-automatica',
    title: '5 Razões para Automatizar a Verificação de EPIs',
    excerpt: 'Por que empresas líderes estão abandonando checklists manuais e adotando visão computacional.',
    category: 'Segurança',
    tags: ['EPI', 'Visão Computacional', 'Compliance'],
    author: { name: 'Roberto Lima', role: 'Engenheiro de Segurança' },
    publishedAt: new Date('2025-11-18'),
    readTime: 6,
    featured: false,
  },
  {
    id: '4',
    slug: 'produtividade-construcao-civil',
    title: 'Aumentando a Produtividade em Canteiros de Obra com IA',
    excerpt: 'Estudo de caso: como uma construtora aumentou 23% a produtividade usando WorkStream.',
    category: 'Cases',
    tags: ['Construção Civil', 'Produtividade', 'Case'],
    author: { name: 'Mariana Costa', role: 'Customer Success' },
    publishedAt: new Date('2025-11-15'),
    readTime: 10,
    featured: false,
  },
  {
    id: '5',
    slug: 'lgpd-monitoramento-trabalho',
    title: 'LGPD e Monitoramento de Colaboradores: O Que Você Precisa Saber',
    excerpt: 'Como implementar monitoramento de forma ética e em conformidade com a legislação brasileira.',
    category: 'Compliance',
    tags: ['LGPD', 'Privacidade', 'Compliance'],
    author: { name: 'Dr. Paulo Santos', role: 'Advogado Trabalhista' },
    publishedAt: new Date('2025-11-10'),
    readTime: 15,
    featured: false,
  },
  {
    id: '6',
    slug: 'nr-10-eletricidade',
    title: 'NR-10: Segurança em Serviços com Eletricidade',
    excerpt: 'Guia prático para implementar a NR-10 e proteger colaboradores que trabalham com eletricidade.',
    category: 'NRs',
    tags: ['NR-10', 'Eletricidade', 'Segurança'],
    author: { name: 'Carlos Mendes', role: 'Especialista em Segurança' },
    publishedAt: new Date('2025-11-05'),
    readTime: 14,
    featured: false,
  },
]

const categories = ['Todos', 'NRs', 'Tecnologia', 'Segurança', 'Cases', 'Compliance']

export default function Blog() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('Todos')
  
  const featuredPosts = posts.filter(p => p.featured)
  const regularPosts = posts.filter(p => !p.featured)
  
  const filteredPosts = regularPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'Todos' || post.category === selectedCategory
    return matchesSearch && matchesCategory
  })
  
  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-br from-cyan-500/20 to-transparent rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Blog WorkStream
            </h1>
            <p className="text-xl text-slate-400 mb-8">
              Artigos, guias e novidades sobre segurança do trabalho, tecnologia e produtividade
            </p>
            
            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar artigos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Posts */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Em Destaque</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredPosts.map(post => (
              <FeaturedPostCard key={post.id} post={post} />
            ))}
          </div>
        </div>
      </section>
      
      {/* Categories & Posts */}
      <section className="py-12 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          {/* Categories */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`
                  px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors
                  ${selectedCategory === cat
                    ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }
                `}
              >
                {cat}
              </button>
            ))}
          </div>
          
          {/* Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map(post => (
              <PostCard key={post.id} post={post} />
            ))}
          </div>
          
          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-400">Nenhum artigo encontrado.</p>
            </div>
          )}
        </div>
      </section>
      
      {/* Newsletter */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Receba novidades por email
            </h2>
            <p className="text-slate-400 mb-6">
              Assine nossa newsletter e receba artigos, guias e atualizações sobre segurança do trabalho.
            </p>
            
            <form className="flex gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="seu@email.com"
                className="flex-1 px-4 py-3 rounded-xl bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
              />
              <button
                type="submit"
                className="px-6 py-3 rounded-xl font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)' }}
              >
                Assinar
              </button>
            </form>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}

function FeaturedPostCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group relative rounded-2xl overflow-hidden bg-slate-800/50 border border-slate-700 hover:border-cyan-500/30 transition-all"
    >
      {/* Image placeholder */}
      <div className="h-48 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center">
        <span className="text-6xl opacity-50">📰</span>
      </div>
      
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-1 rounded-lg text-xs font-medium bg-cyan-500/10 text-cyan-400">
            {post.category}
          </span>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {post.readTime} min
          </span>
        </div>
        
        <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
          {post.title}
        </h3>
        
        <p className="text-slate-400 mb-4 line-clamp-2">
          {post.excerpt}
        </p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <User className="w-4 h-4" />
            {post.author.name}
          </div>
          <div className="flex items-center gap-1 text-sm text-slate-500">
            <Calendar className="w-4 h-4" />
            {post.publishedAt.toLocaleDateString('pt-BR')}
          </div>
        </div>
      </div>
    </Link>
  )
}

function PostCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group p-6 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-cyan-500/30 transition-all"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="px-2 py-1 rounded-lg text-xs font-medium bg-slate-700 text-slate-300">
          {post.category}
        </span>
        <span className="text-xs text-slate-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {post.readTime} min
        </span>
      </div>
      
      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors line-clamp-2">
        {post.title}
      </h3>
      
      <p className="text-sm text-slate-400 mb-4 line-clamp-2">
        {post.excerpt}
      </p>
      
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{post.author.name}</span>
        <span>{post.publishedAt.toLocaleDateString('pt-BR')}</span>
      </div>
      
      {/* Tags */}
      <div className="flex flex-wrap gap-1 mt-3">
        {post.tags.slice(0, 3).map(tag => (
          <span key={tag} className="px-2 py-0.5 rounded text-xs bg-slate-700/50 text-slate-400">
            #{tag}
          </span>
        ))}
      </div>
    </Link>
  )
}

